<template>
  <div class="grid-headline">
    <div class="select-columns-container">
      <b-dropdown aria-role="menu" trap-focus :mobile-modal="false" @active-change="onColumnsDropdownActiveChange">
        <template #trigger>
          <button class="is-transparent" aria-haspopup="true" aria-controls="dropdown-menu" :class="{ active: showSelectColumns }" @click="showSelectColumns = !showSelectColumns">
            <span class="button-text-w">
              <span class="text">{{ $t("Grid.ColumnsSelect") }}</span>
            </span>
            <icon-base width="12" height="7" view-box="0 0 12 7" icon-name="caret-down"><icon-caret-down /></icon-base>
          </button>
        </template>

        <b-dropdown-item aria-role="menu-item" :focusable="false" custom paddingless>
          <div v-for="(item, index) in columns" :key="index">
            <b-field :class-name="index">
              <b-checkbox v-model="columns[index].visible" :native-value="item.id" @change.native="onColumnSelect(columns[index])">
                {{ item.label }}
              </b-checkbox>
            </b-field>
          </div>
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div class="border"></div>

    <mq-layout class="search-container" mq="lg">
      <b-tooltip position="is-bottom" multilined type="is-dark">
        <b-field>
          <icon-base width="15" height="15" view-box="0 0 15 15" icon-name="search"><icon-search /></icon-base>
          <b-input :value="searchValue" :placeholder="$t('Grid.Search')" @blur="onSearchChange($event)" @keyup.native.enter="onSearchChange($event)"></b-input>
        </b-field>
        <template v-slot:content>
          <p>{{ $t("Phones.Tooltips.GridHeadline.Search") }}</p>
        </template>
      </b-tooltip>
    </mq-layout>
  </div>
</template>

<script lang="ts">
import VueMq from "vue-mq";
import { Component, Prop, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { GridColumn } from "@/models/GridColumn";
import { PhoneWithChannel } from "@/models/phones/PhoneWithChannel";
import { Result } from "@/models/Result";
import { TableSettings, TableSettingsStateColumn } from "@/models/TableSettings";
import HelperService from "@/services/HelperService";
import ApiService from "@/services/api/ApiService";
import IconBase from "../IconBase.vue";
import IconSearch from "../icons/IconSearch.vue";
import IconCaretDown from "../icons/IconCaretDown.vue";

Vue.use(VueMq, {
  breakpoints: {
    sm: 576,
    md: 992,
    lg: Infinity,
  },
  defaultBreakpoint: "sm",
});

const namespace = "phonesModule";

@Component({
  components: {
    IconBase,
    IconSearch,
    IconCaretDown,
  },
})
export default class PhonesGridHeadline extends Vue {
  
  //#region Vuex getters and actions

  @Getter("columns", { namespace }) columns!: GridColumn[];
  @Getter("phones", { namespace }) phones!: PhoneWithChannel[];
  @Getter("searchValue", { namespace }) searchValue!: string;
  @Getter("tableSettings", { namespace }) tableSettings!: TableSettings;
  @Action("setSearchValue", { namespace }) setSearchValue!: (searchValue: string) => void;
  @Action("setSelectedPhonesGridColumnsIds", { namespace }) setSelectedPhonesGridColumnsIds!: (selectedPhonesGridColumnsIds: number[]) => void;
  
  //#endregion Vuex getters and actions
  

  //#region Properties

  showSelectColumns = false;

  //#endregion Properties


  //#region Handlers

  onColumnsDropdownActiveChange(show: boolean) {
    if (show === false) {
      ApiService.saveTableSettings({
        culture: this.tableSettings.Culture,
        gridId: this.tableSettings.GridId,
        optionsJson: JSON.stringify(this.tableSettings.StateObject),
        url: this.tableSettings.Url
      }).then((result: Result) => {
        if (!result.Success) {
          HelperService.showError(result.Message);
        }
      });
    }
  }

  onColumnSelect(column: GridColumn) {
    const selectedGridColumnsIdsNew = this.columns.filter((i: GridColumn) => i.visible)
          .map((i: GridColumn) => i.id);
    this.setSelectedPhonesGridColumnsIds(selectedGridColumnsIdsNew);
    column.sortable = column.visible;

    let settingsColumn: TableSettingsStateColumn | undefined = this.tableSettings.StateObject.columns.find(this.findTableSettingsColumn(column));
    if (settingsColumn) {
      HelperService.onColumnSelectInitSettingsColumn(settingsColumn, column);
    } else {
      settingsColumn = HelperService.createTableSettingsColumn(column);
      this.tableSettings.StateObject.columns.push(settingsColumn);
    }
    settingsColumn = this.tableSettings.StateObject.dataSource.fields.find(this.findTableSettingsColumn(column));
    if (settingsColumn) {
      HelperService.onColumnSelectInitSettingsColumn(settingsColumn, column);
    } else {
      settingsColumn = HelperService.createTableSettingsColumn(column);
      this.tableSettings.StateObject.dataSource.fields.push(settingsColumn);
    }
  }

  onSearchChange(e: Event) {
    this.setSearchValue((e.target as HTMLInputElement).value);
  }

  //#endregion Handlers


  //#region Private methods

  private findTableSettingsColumn(column: GridColumn): (settingsColumn: TableSettingsStateColumn) => boolean {
    return (settingsColumn: TableSettingsStateColumn) => {
      let result = settingsColumn.field && settingsColumn.field === column.name
            || !settingsColumn.field && settingsColumn.title.toLowerCase() === column.label.toLowerCase();
      return result;
    }
  }

  //#endregion Private methods
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.grid-headline {
  display: flex;
  align-items: center;
  padding: 18px 21px;
  border-bottom: 1px solid #d3d6e2;
  position: relative;
  z-index: 1;
  @media (max-width: 991px) {
    padding: 14px 10px 14px 15px;
  }
  @media (max-width: 399px) {
    justify-content: space-between;
  }
  @media (max-width: 359px) {
    display: block;
    padding: 0;
  }
  .select-columns-container {
    padding-left: 20px;
    position: relative;
    @media (max-width: 991px) {
      padding-left: 15px;
    }
    @media (max-width: 399px) {
      padding-left: 5px;
    }
    @media (max-width: 359px) {
      padding: 15px 15px;
      border-bottom: 1px solid $lightd;
    }
    .button-text-w {
      margin-left: 11px;
    }
    .dropdown-menu {
      top: 46px;
      right: -22px;
      @media (max-width: 991px) {
        top: 43px;
        right: -11px;
      }
      @media (max-width: 359px) {
        top: 39px;
        right: -16px;
      }

      .dropdown-content {
        border: 1px solid #d3d6e2;
        box-shadow: 0px 0px 4px #d3d6e2;
        width: 529px;
        padding: 25px 25px 10px;
        @media (max-width: 991px) {
          border-radius: 0;
        }
        @media (max-width: 575px) {
          width: 384px;
          padding: 15px 15px 0;
        }
        @media (max-width: 399px) {
          width: 345px;
        }
        @media (max-width: 360px) {
          width: 330px;
        }

        @media (max-width: 359px) {
          width: 290px;
        }
      }
      .dropdown-item {
        columns: 3;
        @media (max-width: 575px) {
          columns: 2;
        }
        .field {
          margin-bottom: 14px;
          @media (max-width: 575px) {
            margin-bottom: 12px;
          }
        }
      }
    }
  }
  .border {
    display: block;
    height: 33px;
    width: 1px;
    background: #dcdcdc;
    @media (max-width: 991px) {
      height: 35px;
    }
    @media (max-width: 359px) {
      display: none;
    }
  }
  .search-container {
    padding: 0 25px;
    flex: 1;
    margin: 0;
    .input {
      border: none;
      box-shadow: none;
      padding: 0 9px;
      height: 33px;
    }
  }
  .export-button-container {
    padding-right: 20px;
    @media (max-width: 399px) {
      padding-right: 10px;
    }
    @media (max-width: 359px) {
      padding: 15px 15px;
    }
    .export-button {
      border-color: #269339;
      color: #269339;
      @media (max-width: 991px) {
        font-size: 16px;
        height: 35px;
        min-width: 120px;
      }
      &:hover {
        color: #fff;
        background: #269339;
      }
      > span {
        display: flex;
        align-items: center;
        .text {
          margin-right: 9px;
        }
      }
    }
  }
}

html[dir="ltr"] {
  .grid-headline {
    @media (max-width: 991px) {
      padding: 14px 15px 14px 10px;
    }
    .select-columns-container {
      padding: 0 20px 0 0;
      @media (max-width: 991px) {
        padding: 0 15px 0 0;
      }
      @media (max-width: 399px) {
        padding: 0 5px 0 0;
      }
      @media (max-width: 359px) {
        padding: 15px 15px;
      }
      .button-text-w {
        margin: 0 11px 0 0;
      }
    }
    .export-button-container {
      padding: 0 0 0 20px;
      @media (max-width: 399px) {
        padding: 0 0 0 10px;
      }
      @media (max-width: 359px) {
        padding: 15px 15px;
      }
    }
  }
}
</style>
